// ═══════════════════════════════════════════════════════════════
// AI POLICE — DEMO RESPONSES
// Pre-built responses per module per workflow stap (NL + EN)
// Later vervangen door Mistral AI responses
// ═══════════════════════════════════════════════════════════════

export const WELCOME_MESSAGES = {
  agent: {
    nl: `Welkom bij **AI Police — Agent Modus**

Selecteer een werkproces in het menu om te beginnen. Ik begeleid u stap voor stap door het administratieve proces.

**Beschikbare categorieën:**
- 🔴 **Kern:** Aangifte & PV, Dossiervorming
- 🟠 **Onderzoek:** Verhoor, Incident, Camera, Beslag
- 🟢 **Operationeel:** Signaleringen, Briefings, Planning, Vergunningen
- 🟣 **Kennis:** Wet & Procedures, Slachtofferhulp

Alle gesprekken worden versleuteld opgeslagen conform BIO-normen.`,
    en: `Welcome to **AI Police — Agent Mode**

Select a workflow in the menu to begin. I will guide you step by step through the administrative process.

**Available categories:**
- 🔴 **Core:** Report & PV, Case File Assembly
- 🟠 **Investigation:** Interview, Incident, Camera, Seizure
- 🟢 **Operational:** Alerts, Briefings, Scheduling, Permits
- 🟣 **Knowledge:** Law & Procedures, Victim Support

All conversations are encrypted and stored in accordance with BIO standards.`,
  },
  burger: {
    nl: `Welkom bij de **Politie AI Assistent**

Ik help u graag verder. Kies hieronder waarmee ik u kan helpen:

- 📝 **Aangifte doen** — hulp bij het doen van aangifte
- ⚖️ **Rechten & Plichten** — wat zijn uw rechten bij politiecontact
- 💻 **Cybercrime Hulp** — gehackt, phishing, identiteitsfraude
- 🤝 **Slachtofferhulp** — hulporganisaties en schadevergoeding

⚠️ **Noodgeval? Bel 112.** Dit is een informatiedienst, geen vervanging voor politiecontact.`,
    en: `Welcome to the **Police AI Assistant**

I am happy to help you. Please select how I can assist you:

- 📝 **File a Report** — help with filing a report
- ⚖️ **Rights & Duties** — your rights during police contact
- 💻 **Cybercrime Help** — hacked, phishing, identity fraud
- 🤝 **Victim Support** — support organizations & compensation

⚠️ **Emergency? Call 112.** This is an information service, not a replacement for police contact.`,
  },
};

// ═══════════════════════════════════════════════════════════════
// MODULE WELCOME MESSAGES (shown when a module is selected)
// ═══════════════════════════════════════════════════════════════

export const MODULE_WELCOME = {
  // ── KERN ──
  aangifte: {
    nl: `📝 **Aangifte & PV — Werkproces gestart**

Ik begeleid u bij het opnemen van een aangifte en het genereren van een proces-verbaal. Het werkproces bestaat uit 5 stappen:

1. Type delict selecteren
2. Verklaring opnemen
3. Bewijsmateriaal toevoegen
4. PV concept genereren
5. Review & ondertekening

**Stap 1:** Selecteer het type delict waarvoor aangifte wordt gedaan.`,
    en: `📝 **Report & PV — Workflow started**

I will guide you through filing a report and generating an official statement. The workflow consists of 5 steps:

1. Select offense type
2. Take statement
3. Add evidence
4. Generate PV draft
5. Review & sign

**Step 1:** Select the type of offense being reported.`,
  },
  dossier: {
    nl: `📋 **Dossiervorming — Werkproces gestart**

Ik help u bij het samenstellen van een compleet strafdossier voor het Openbaar Ministerie. Een volledig dossier bevat: proces-verbalen, verhoorverslagen, bewijsstukken, forensische rapporten en een zaaksamenvatting.

**Stap 1:** Selecteer de zaak waarvoor u een dossier wilt samenstellen.`,
    en: `📋 **Case File Assembly — Workflow started**

I will help you assemble a complete criminal case file for the Public Prosecutor. A complete file contains: official statements, interview reports, evidence, forensic reports, and a case summary.

**Step 1:** Select the case for which you want to assemble a file.`,
  },

  // ── ONDERZOEK ──
  verhoor: {
    nl: `🎙️ **Verhoor Transcriptie — Werkproces gestart**

Real-time transcriptie en analyse van verhoren. Het systeem transcribeert spraak, identificeert inconsistenties en genereert automatisch een samenvatting.

⚖️ *Herinnering: lees de verdachte zijn/haar rechten voor bij aanvang van het verhoor (art. 29 Sv).*

**Stap 1:** Start de opname of typ handmatig mee.`,
    en: `🎙️ **Interview Transcript — Workflow started**

Real-time transcription and analysis of interviews. The system transcribes speech, identifies inconsistencies, and automatically generates a summary.

⚖️ *Reminder: read the suspect their rights before starting the interview (art. 29 Sv).*

**Step 1:** Start recording or type manually.`,
  },
  incident: {
    nl: `🚨 **Incident Registratie — Werkproces gestart**

Spraak-gestuurde incidentregistratie. Spreek het incident in, en ik structureer het automatisch voor verwerking in het BVH-systeem.

**Stap 1:** Spreek het incident in of typ een beschrijving.`,
    en: `🚨 **Incident Registration — Workflow started**

Voice-driven incident registration. Dictate the incident, and I will automatically structure it for processing in the BVH system.

**Step 1:** Dictate the incident or type a description.`,
  },
  camera: {
    nl: `📹 **Camerabeelden — Werkproces gestart**

Verwerk en beschrijf camerabeelden voor opname in het strafdossier. Het systeem helpt bij het markeren van relevante fragmenten en genereert beschrijvingen.

**Stap 1:** Selecteer de beelden die u wilt verwerken.`,
    en: `📹 **Camera Footage — Workflow started**

Process and describe camera footage for inclusion in the case file. The system helps mark relevant fragments and generates descriptions.

**Step 1:** Select the footage you want to process.`,
  },
  beslag: {
    nl: `🏷️ **Beslagname — Werkproces gestart**

Registreer in beslag genomen goederen conform de richtlijnen. Elk goed wordt voorzien van een unieke code, beschrijving, foto's en bewaarlocatie.

**Stap 1:** Selecteer het type goed dat in beslag is genomen.`,
    en: `🏷️ **Seizure — Workflow started**

Register seized goods according to guidelines. Each item receives a unique code, description, photos, and storage location.

**Step 1:** Select the type of item that has been seized.`,
  },

  // ── OPERATIONEEL ──
  signalering: {
    nl: `🔍 **Signaleringen — Werkproces gestart**

Maak en verspreid signaleringen voor personen, voertuigen of objecten. Signaleringen worden gekoppeld aan lopende zaken.

**Stap 1:** Kies het type signalering.`,
    en: `🔍 **Alerts — Workflow started**

Create and distribute alerts for persons, vehicles, or objects. Alerts are linked to active cases.

**Step 1:** Choose the alert type.`,
  },
  briefing: {
    nl: `📊 **Briefings — Werkproces gestart**

Genereer een dienst-overdracht briefing op basis van de incidenten en meldingen van de afgelopen dienst. Inclusief aandachtspunten en actiepunten.

**Stap 1:** Selecteer de dienstperiode.`,
    en: `📊 **Briefings — Workflow started**

Generate a shift handover briefing based on incidents and reports from the previous shift. Including highlights and action items.

**Step 1:** Select the shift period.`,
  },
  planning: {
    nl: `📅 **Planning & Roostering — Werkproces gestart**

Beheer dienstplanning, controleer bezetting en los planningsknelpunten op. Het systeem signaleert onderbezetting en stelt oplossingen voor.

**Stap 1:** Kies de dienstperiode.`,
    en: `📅 **Scheduling — Workflow started**

Manage shift planning, check staffing levels, and resolve scheduling conflicts. The system flags understaffing and suggests solutions.

**Step 1:** Choose the shift period.`,
  },
  vergunning: {
    nl: `📜 **Vergunningen — Werkproces gestart**

Beoordeel vergunningaanvragen en genereer adviezen. Het systeem controleert antecedentengegevens en toetst aan de geldende regelgeving.

**Stap 1:** Selecteer het type vergunningaanvraag.`,
    en: `📜 **Permits — Workflow started**

Review permit applications and generate recommendations. The system checks background records and tests against applicable regulations.

**Step 1:** Select the permit application type.`,
  },

  // ── KENNIS ──
  kennis: {
    nl: `⚖️ **Wet & Procedures — Kennisbank gestart**

Doorzoek wetgeving, protocollen, procedures en statistieken. De kennisbank bevat het Wetboek van Strafrecht, Strafvordering, de Politiewet en relevante AMvB's.

**Stap 1:** Kies een rechtsgebied of stel direct een vraag.`,
    en: `⚖️ **Law & Procedures — Knowledge base started**

Search legislation, protocols, procedures, and statistics. The knowledge base contains the Criminal Code, Criminal Procedure, the Police Act, and relevant regulations.

**Step 1:** Choose a legal area or ask a question directly.`,
  },
  slachtoffer: {
    nl: `🤝 **Slachtofferhulp — Werkproces gestart**

Ik help u bij het doorverwijzen en ondersteunen van slachtoffers. Op basis van het type misdrijf geef ik informatie over rechten, beschikbare hulp en genereer ik een doorverwijsbrief.

**Stap 1:** Beschrijf het type misdrijf.`,
    en: `🤝 **Victim Support — Workflow started**

I will help you with referring and supporting victims. Based on the type of crime, I provide information about rights, available help, and generate a referral letter.

**Step 1:** Describe the type of crime.`,
  },

  // ── BURGER ──
  b_aangifte: {
    nl: `📝 **Aangifte Doen**

Ik help u stap voor stap bij het doen van aangifte. Na afloop ontvangt u een samenvatting die u kunt meenemen naar het politiebureau.

⚠️ *Bij spoed of gevaar: bel 112.*

**Wat is er gebeurd?** Kies hieronder het type incident.`,
    en: `📝 **File a Report**

I will help you step by step with filing a report. Afterwards, you will receive a summary that you can bring to the police station.

⚠️ *In case of emergency or danger: call 112.*

**What happened?** Choose the type of incident below.`,
  },
  b_rechten: {
    nl: `⚖️ **Rechten & Plichten**

Kent u uw rechten bij contact met de politie? Ik leg uit wat de politie wel en niet mag, en wat uw rechten zijn in verschillende situaties.

**Kies een situatie** waarin u meer wilt weten over uw rechten.`,
    en: `⚖️ **Rights & Duties**

Do you know your rights when dealing with the police? I explain what the police can and cannot do, and what your rights are in various situations.

**Choose a situation** where you want to know more about your rights.`,
  },
  b_cyber: {
    nl: `💻 **Cybercrime Hulp**

Online criminaliteit neemt toe. Ik help u met directe beveiligingsstappen en begeleid u bij het doen van aangifte.

**Wat is er gebeurd?** Kies het type cybercrime.`,
    en: `💻 **Cybercrime Help**

Online crime is increasing. I will help you with immediate security steps and guide you through filing a report.

**What happened?** Choose the type of cybercrime.`,
  },
  b_slachtoffer: {
    nl: `🤝 **Slachtofferhulp**

Bent u slachtoffer geworden van een misdrijf? U staat er niet alleen voor. Ik help u bij het vinden van de juiste hulp en ondersteuning.

**Beschrijf uw situatie** — kies hieronder wat het meest van toepassing is.`,
    en: `🤝 **Victim Support**

Have you been a victim of crime? You are not alone. I will help you find the right help and support.

**Describe your situation** — choose what applies most below.`,
  },
};

// ═══════════════════════════════════════════════════════════════
// STEP RESPONSES — Context-aware per module per workflow stap
// ═══════════════════════════════════════════════════════════════

export const STEP_RESPONSES = {
  aangifte: {
    select_inbraak: {
      nl: `**Type delict: Woninginbraak (art. 311 Sr)**

Woninginbraak is een gekwalificeerd vermogensdelict. De volgende gegevens zijn vereist voor het PV:

- Datum en tijdstip (wanneer ontdekt / wanneer gepleegd)
- Exacte locatie (adres, verdieping, welk vertrek)
- Wijze van binnenkomst (braaksporen, insluitsporen)
- Gestolen goederen (omschrijving + geschatte waarde)
- Mogelijke getuigen

**Stap 2:** Ik neem nu de verklaring op. U kunt spreken of typen.`,
      en: `**Offense type: Residential burglary (art. 311 Sr)**

Residential burglary is a qualified property crime. The following information is required for the PV:

- Date and time (when discovered / when committed)
- Exact location (address, floor, which room)
- Method of entry (break-in marks, lock traces)
- Stolen goods (description + estimated value)
- Possible witnesses

**Step 2:** I will now take the statement. You can speak or type.`,
    },
    select_diefstal: {
      nl: `**Type delict: Diefstal (art. 310 Sr)**

De volgende gegevens zijn vereist:

- Datum, tijdstip en locatie
- Omschrijving van het gestolen goed
- Geschatte waarde
- Omstandigheden (wel/geen geweld, aantal daders)
- Signalement dader(s) indien bekend

**Stap 2:** Ik neem nu de verklaring op. U kunt spreken of typen.`,
      en: `**Offense type: Theft (art. 310 Sr)**

The following information is required:

- Date, time, and location
- Description of stolen property
- Estimated value
- Circumstances (with/without violence, number of perpetrators)
- Description of perpetrator(s) if known

**Step 2:** I will now take the statement. You can speak or type.`,
    },
    select_cyber: {
      nl: `**Type delict: Cybercrime (art. 138ab / 317 / 326 Sr)**

Cybercrime kent meerdere vormen. Specificeer het type:

- **Hacken** (art. 138ab Sr) — ongeautoriseerde toegang
- **Ransomware** (art. 317 Sr) — digitale afpersing
- **Phishing/Fraude** (art. 326 Sr) — oplichting

Vereiste gegevens: screenshots, e-mails, transactiegegevens, IP-adressen indien bekend.

**Stap 2:** Beschrijf wat er precies is gebeurd.`,
      en: `**Offense type: Cybercrime (art. 138ab / 317 / 326 Sr)**

Cybercrime has multiple forms. Specify the type:

- **Hacking** (art. 138ab Sr) — unauthorized access
- **Ransomware** (art. 317 Sr) — digital extortion
- **Phishing/Fraud** (art. 326 Sr) — scam/deception

Required data: screenshots, emails, transaction data, IP addresses if known.

**Step 2:** Describe exactly what happened.`,
    },
    dictate: {
      nl: `🎤 **Spraakopname actief**

Ik luister mee en structureer uw verklaring automatisch. Spreek duidelijk en in chronologische volgorde:

1. Begin met **wanneer** het incident plaatsvond
2. Beschrijf **waar** het gebeurde
3. Vertel **wat** er precies is voorgevallen
4. Beschrijf eventuele **dader(s)**
5. Noem eventuele **getuigen**

*Tip: u kunt pauzeren en later doorgaan. De tekst wordt real-time getoond.*`,
      en: `🎤 **Voice recording active**

I am listening and will automatically structure your statement. Speak clearly and in chronological order:

1. Start with **when** the incident occurred
2. Describe **where** it happened
3. Tell **what** exactly took place
4. Describe any **perpetrator(s)**
5. Mention any **witnesses**

*Tip: you can pause and continue later. Text is shown in real-time.*`,
    },
    add_witness: {
      nl: `👤 **Getuige toevoegen**

Vul de getuigegegevens in:

- Volledige naam
- Geboortedatum
- Adres
- Telefoonnummer
- Relatie tot aangever
- Korte beschrijving van wat de getuige heeft waargenomen

*Meerdere getuigen? Na het toevoegen kunt u nog een getuige toevoegen.*`,
      en: `👤 **Add witness**

Fill in the witness details:

- Full name
- Date of birth
- Address
- Phone number
- Relationship to complainant
- Brief description of what the witness observed

*Multiple witnesses? After adding, you can add another witness.*`,
    },
    view_draft: {
      nl: `📋 **Concept verklaring**

Op basis van de tot nu toe verzamelde informatie is het volgende concept opgesteld:

---
**VERKLARING**
Aangever verklaart het volgende:

*[De verklaring wordt hier automatisch samengesteld op basis van de ingevoerde gegevens en spraakopname]*

---

U kunt de tekst aanpassen voordat we doorgaan naar bewijsmateriaal.`,
      en: `📋 **Draft statement**

Based on the information collected so far, the following draft has been prepared:

---
**STATEMENT**
The complainant declares the following:

*[The statement is automatically assembled based on entered data and voice recording]*

---

You can edit the text before we proceed to evidence.`,
    },
    next_step: {
      nl: `➡️ **Door naar PV generatie**

Alle benodigde informatie is verzameld:
- ✅ Type delict geselecteerd
- ✅ Verklaring opgenomen
- ✅ Bewijsmateriaal gekoppeld

Ik genereer nu een concept proces-verbaal (PV) op basis van alle verzamelde gegevens.

*Het PV volgt de standaard PV-template conform de richtlijnen.*`,
      en: `➡️ **Proceeding to PV generation**

All required information has been collected:
- ✅ Offense type selected
- ✅ Statement recorded
- ✅ Evidence linked

I am now generating a draft official statement (PV) based on all collected data.

*The PV follows the standard PV template according to guidelines.*`,
    },
    approve: {
      nl: `✅ **PV goedgekeurd**

Het proces-verbaal is goedgekeurd en klaar voor ondertekening. De volgende stappen:

1. Digitale ondertekening door verbalisant
2. Ondertekening door aangever (indien van toepassing)
3. Opname in het dossier
4. Doorzending naar het OM (indien relevant)

*Het PV is opgeslagen met registratienummer PV-2026-[auto].*`,
      en: `✅ **PV approved**

The official statement has been approved and is ready for signing. Next steps:

1. Digital signature by officer
2. Signature by complainant (if applicable)
3. Inclusion in case file
4. Forwarding to prosecutor (if relevant)

*The PV is saved with registration number PV-2026-[auto].*`,
    },
  },

  // ── DOSSIER DEFAULT ──
  dossier: {
    search: {
      nl: `🔍 **Zaak zoeken**\n\nVoer een zaaknummer, naam van verdachte, of delicttype in. Ik doorzoek de gekoppelde zaken en toon de resultaten.\n\n*Voorbeeld: "2026-AB-12345" of "inbraak Arnhem januari 2026"*`,
      en: `🔍 **Search case**\n\nEnter a case number, suspect name, or offense type. I will search linked cases and show results.\n\n*Example: "2026-AB-12345" or "burglary Arnhem January 2026"*`,
    },
    recent: {
      nl: `📁 **Recente zaken**\n\n| # | Zaaknummer | Delict | Datum | Status |\n|---|-----------|--------|-------|--------|\n| 1 | 2026-AB-12345 | Woninginbraak | 08-02-2026 | Open |\n| 2 | 2026-AB-12298 | Diefstal fiets | 06-02-2026 | PV gereed |\n| 3 | 2026-AB-12187 | Mishandeling | 03-02-2026 | Bij OM |\n\n*Selecteer een zaak om het dossier te openen.*`,
      en: `📁 **Recent cases**\n\n| # | Case number | Offense | Date | Status |\n|---|-----------|--------|-------|--------|\n| 1 | 2026-AB-12345 | Residential burglary | 08-02-2026 | Open |\n| 2 | 2026-AB-12298 | Bicycle theft | 06-02-2026 | PV ready |\n| 3 | 2026-AB-12187 | Assault | 03-02-2026 | At prosecutor |\n\n*Select a case to open the file.*`,
    },
  },

  // ── VERHOOR ──
  verhoor: {
    record: {
      nl: `🎙️ **Verhoor opname gestart**\n\n⚖️ *Vergeet niet: de cautie (art. 29 Sv) moet zijn voorgelezen.*\n\n"U bent niet verplicht tot antwoorden. Alles wat u zegt kan als bewijs worden gebruikt."\n\nDe opname loopt. Ik transcribeer real-time en markeer automatisch:\n- Wisselingen spreker (verhoorder / verdachte)\n- Emotionele uitingen\n- Stiltes > 10 seconden\n\n*Druk op ⏸️ om te pauzeren.*`,
      en: `🎙️ **Interview recording started**\n\n⚖️ *Reminder: the caution (art. 29 Sv) must have been read.*\n\n"You are not obligated to answer. Everything you say can be used as evidence."\n\nRecording is running. I transcribe in real-time and automatically mark:\n- Speaker changes (interviewer / suspect)\n- Emotional expressions\n- Silences > 10 seconds\n\n*Press ⏸️ to pause.*`,
    },
    rights: {
      nl: `⚖️ **Rechten verdachte (art. 29 Sv)**\n\nVóór aanvang van het verhoor moet de verdachte worden gewezen op:\n\n1. **Zwijgrecht** — u bent niet verplicht tot antwoorden\n2. **Recht op bijstand advocaat** — u heeft recht op overleg met een raadsman\n3. **Recht op vertolking** — indien u de taal niet machtig bent\n4. **Cautie** — alles wat u zegt kan als bewijs worden gebruikt\n\n*Deze rechten zijn vastgelegd in het Wetboek van Strafvordering.*`,
      en: `⚖️ **Suspect's rights (art. 29 Sv)**\n\nBefore starting the interview, the suspect must be informed of:\n\n1. **Right to silence** — you are not obligated to answer\n2. **Right to legal counsel** — you have the right to consult a lawyer\n3. **Right to interpretation** — if you do not speak the language\n4. **Caution** — everything you say can be used as evidence\n\n*These rights are established in the Code of Criminal Procedure.*`,
    },
  },

  // ── INCIDENT ──
  incident: {
    dictate: {
      nl: `🎤 **Spreek het incident in**\n\nIk luister en structureer automatisch. Probeer het volgende te benoemen:\n\n- **Wat:** type incident (ongeval, aanhouding, melding, etc.)\n- **Waar:** exacte locatie (straat, huisnummer, stad)\n- **Wanneer:** datum en tijdstip\n- **Wie:** betrokkenen (namen, signalement)\n- **Hoe:** toedracht en omstandigheden\n\n*Real-time transcriptie loopt...*`,
      en: `🎤 **Dictate the incident**\n\nI am listening and will structure automatically. Try to include:\n\n- **What:** incident type (accident, arrest, report, etc.)\n- **Where:** exact location (street, number, city)\n- **When:** date and time\n- **Who:** involved parties (names, description)\n- **How:** circumstances\n\n*Real-time transcription running...*`,
    },
  },

  // ── GENERIEKE FALLBACK RESPONSES ──
  _default: {
    nl: `Ik verwerk uw invoer. In de productieversie wordt dit door Mistral AI afgehandeld met de volledige kennisbank. Klik op een van de actieknoppen hieronder om door te gaan.`,
    en: `I am processing your input. In the production version, this will be handled by Mistral AI with the full knowledge base. Click one of the action buttons below to continue.`,
  },
};
